/**
 * PropertiesPanel — thin orchestrator, 3-tab right panel.
 * All heavy sub-components are in ./props/
 */
import React, { useState, useMemo } from "react";
import { getWallMetrics, getRoomMetrics, getProjectBOQ } from "../../engine/metrics";
import { mutUpdateDrawing } from "../../engine/layerMutations";
import { mkEmpty } from "../../engine/dcel";
import { mono, EditRow } from "./props/PanelAtoms";
import { NorthCompass, OriginPanel } from "./props/DrawingTab";
import { LayoutSummary } from "./props/SummaryTab";
import { WallPanel, RoomPanel, SymbolPanel, AnnotationPanel } from "./props/SelectionTab";

const TABS = [
  { id: "summary",   label: "Summary",  icon: "≡" },
  { id: "selection", label: "Selected", icon: "⊡" },
  { id: "drawing",   label: "Drawing",  icon: "📐" },
];

export default function PropertiesPanel({
  st, sel, selAnnotation, commit, selMany,
  onDeleteEdge, onDeleteOpening, onResetOrigin,
  showHE, setShowHE,
  canUndo, canRedo, onUndo, onRedo, onClear,
  unitPreset,
  mode,
}) {
  const [tab, setTab] = useState("summary");

  const boq    = useMemo(() => getProjectBOQ(st), [st]);
  const selEdge = sel.edge    ? (st.edges||{})[sel.edge]               : null;
  const selFace = sel.face    ? (st.faces||{})[sel.face]               : null;
  const selNode = sel.node    ? (st.nodes||{})[sel.node]               : null;
  const selOp   = sel.opening ? (st.openings||{})[sel.opening]         : null;
  const selSym  = sel.symbol  ? (st.symbols||{})[sel.symbol]           : null;
  const wallM   = useMemo(() => selEdge ? getWallMetrics(sel.edge,st) : null, [selEdge,sel.edge,st]);
  const roomM   = useMemo(() => selFace ? getRoomMetrics(sel.face,st) : null, [selFace,sel.face,st]);

  const hasSelection = !!(selEdge||selFace||selNode||selOp||selSym||selAnnotation);

  // Auto-switch to selection when user clicks something
  React.useEffect(() => {
    if (hasSelection) setTab("selection");
  }, [sel.edge,sel.face,sel.node,sel.opening,sel.symbol,selAnnotation]); // eslint-disable-line

  const drawing = st.drawing || {};

  return (
    <div style={{
      width:255, background:"#04080f", borderLeft:"1px solid #0d1520",
      display:"flex", flexDirection:"column", flexShrink:0, ...mono,
    }}>
      {/* Tabs */}
      <div style={{ display:"flex", background:"#030508", borderBottom:"1px solid #0d1520", flexShrink:0 }}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{ flex:1, padding:"7px 2px", fontSize:8, fontWeight:700, letterSpacing:0.8,
              background:tab===t.id?"#06111e":"transparent", border:"none",
              borderBottom:tab===t.id?"2px solid #3b82f6":"2px solid transparent",
              color:tab===t.id?"#60a5fa":"#2a3a4a", cursor:"pointer", ...mono }}>
            {t.icon} {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      <div style={{ flex:1, overflowY:"auto" }}>

        {/* ── SUMMARY ── */}
        {tab==="summary" && <LayoutSummary st={st} boq={boq}/>}

        {/* ── MULTI-SELECT SUMMARY ── */}
        {tab==="selection" && selMany && (selMany.edges.length + selMany.nodes.length + selMany.symbols.length + selMany.annotations.length) > 0 && (
          <div style={{ padding:"10px 12px" }}>
            <div style={{ fontSize:8.5, fontWeight:800, letterSpacing:2, color:"#3b82f6",
              padding:"0 0 6px", ...mono }}>MULTI-SELECTION</div>
            {selMany.edges.length > 0 && (
              <div style={{ fontSize:10, color:"#6b7e94", padding:"2px 0", ...mono }}>
                {selMany.edges.length} wall{selMany.edges.length !== 1 ? "s" : ""}
              </div>
            )}
            {selMany.nodes.length > 0 && (
              <div style={{ fontSize:10, color:"#6b7e94", padding:"2px 0", ...mono }}>
                {selMany.nodes.length} node{selMany.nodes.length !== 1 ? "s" : ""}
              </div>
            )}
            {selMany.symbols.length > 0 && (
              <div style={{ fontSize:10, color:"#6b7e94", padding:"2px 0", ...mono }}>
                {selMany.symbols.length} symbol{selMany.symbols.length !== 1 ? "s" : ""}
              </div>
            )}
            {selMany.annotations.length > 0 && (
              <div style={{ fontSize:10, color:"#6b7e94", padding:"2px 0", ...mono }}>
                {selMany.annotations.length} annotation{selMany.annotations.length !== 1 ? "s" : ""}
              </div>
            )}
            <div style={{ marginTop:8, fontSize:8.5, color:"#1e2a36", ...mono }}>
              Del to delete all selected
            </div>
          </div>
        )}

        {/* ── SELECTION ── */}
        {tab==="selection" && (
          <>
            {!hasSelection && (
              <div style={{ padding:"20px 12px", textAlign:"center", color:"#1e2a36" }}>
                <div style={{ fontSize:20, marginBottom:8 }}>⊡</div>
                <div style={{ fontSize:10 }}>Click an element</div>
                <div style={{ fontSize:8.5, marginTop:4, color:"#131924", lineHeight:1.8 }}>
                  Wall · Room · Node · Opening<br/>Symbol · Annotation · Dim
                </div>
              </div>
            )}

            {selEdge && wallM && (
              <WallPanel edge={selEdge} wallM={wallM} sel={sel} commit={commit} st={st}
                selMany={selMany}
                unitPreset={unitPreset}
                mode={mode}
                onDelete={()=>onDeleteEdge(sel.edge)}/>
            )}

            {selFace && roomM && (
              <RoomPanel faceId={sel.face} roomM={roomM} sel={sel} commit={commit}
                unitPreset={unitPreset}
                mode={mode}
                mode={mode}/>
            )}

            {selNode && (
              <>
                <div style={{ fontSize:8.5, fontWeight:800, letterSpacing:2, color:"#4ade80",
                  padding:"6px 12px 3px", ...mono }}>NODE</div>
                <EditRow label="X (mm)" value={selNode.x} type="number"
                  onCommit={v => commit(s => {
                    const n = s.nodes[sel.node]; if (!n) return s;
                    return { ...s, nodes: { ...s.nodes, [sel.node]: { ...n, x: v } } };
                  })}/>
                <EditRow label="Y (mm)" value={selNode.y} type="number"
                  onCommit={v => commit(s => {
                    const n = s.nodes[sel.node]; if (!n) return s;
                    return { ...s, nodes: { ...s.nodes, [sel.node]: { ...n, y: v } } };
                  })}/>
                <div style={{ padding:"3px 12px", fontSize:8.5, color:"#2a3a4a", ...mono }}>
                  {selNode.halfEdgeIds.length / 2} connected edge{selNode.halfEdgeIds.length / 2 !== 1 ? "s" : ""}
                </div>
              </>
            )}

            {selOp && (
              <>
                <div style={{ fontSize:8.5, fontWeight:800, letterSpacing:2,
                  color:"#e8a838", padding:"6px 12px 3px", ...mono }}>
                  {selOp.sillHeight > 0 ? "WINDOW" : "DOOR"}
                </div>
                <EditRow label="Width (mm)" value={selOp.width} type="number" min={200} max={6000} step={50}
                  onCommit={v => commit(s => ({
                    ...s, openings: { ...s.openings, [sel.opening]: { ...s.openings[sel.opening], width: v } }
                  }))}/>
                <EditRow label="Height (mm)" value={selOp.height} type="number" min={600} max={4000} step={50}
                  onCommit={v => commit(s => ({
                    ...s, openings: { ...s.openings, [sel.opening]: { ...s.openings[sel.opening], height: v } }
                  }))}/>
                {selOp.sillHeight >= 0 && (
                  <EditRow label="Sill height (mm)" value={selOp.sillHeight || 0} type="number" min={0} max={2000} step={50}
                    onCommit={v => commit(s => ({
                      ...s, openings: { ...s.openings, [sel.opening]: { ...s.openings[sel.opening], sillHeight: v } }
                    }))}/>
                )}
                <EditRow label="Position (t)" value={Number((selOp.t || 0.5).toFixed(2))} type="number" min={0.05} max={0.95} step={0.05}
                  onCommit={v => commit(s => ({
                    ...s, openings: { ...s.openings, [sel.opening]: { ...s.openings[sel.opening], t: v } }
                  }))}/>
                <div style={{ padding:"6px 12px 4px" }}>
                  <button onClick={() => onDeleteOpening(sel.opening)}
                    style={{ width:"100%", padding:"4px", background:"#140808",
                      border:"1px solid #2a0e0e", color:"#f87171",
                      borderRadius:2, cursor:"pointer", fontSize:8.5, ...mono }}>
                    Delete opening
                  </button>
                </div>
              </>
            )}

            {selSym && <SymbolPanel sym={selSym} sel={sel} commit={commit}/>}

            {selAnnotation && (
              <AnnotationPanel
                selAnnotation={selAnnotation} st={st} commit={commit}
              />
            )}
          </>
        )}

        {/* ── DRAWING ── */}
        {tab==="drawing" && (
          <>
            <div style={{ fontSize:8.5, fontWeight:800, letterSpacing:2, color:"#3b82f6",
              padding:"6px 12px 3px", ...mono }}>NORTH ORIENTATION</div>
            <NorthCompass
              rotation={drawing.northRotation||0}
              onChange={v=>commit(s=>mutUpdateDrawing({northRotation:v},s))}/>

            <div style={{ height:1, background:"#06111e", margin:"8px 0" }}/>

            <div style={{ fontSize:8.5, fontWeight:800, letterSpacing:2, color:"#2a3a4a",
              padding:"6px 12px 3px", ...mono }}>ORIGIN & EXTENTS</div>
            <OriginPanel st={st} onResetOrigin={onResetOrigin}/>

            <div style={{ height:1, background:"#06111e", margin:"8px 0" }}/>

            <div style={{ fontSize:8.5, fontWeight:800, letterSpacing:2, color:"#2a3a4a",
              padding:"6px 12px 3px", ...mono }}>ACTIONS</div>
            <div style={{ padding:"4px 12px", display:"flex", flexDirection:"column", gap:4 }}>
              <div style={{ display:"flex", gap:4 }}>
                <button onClick={onUndo} disabled={!canUndo}
                  style={{ flex:1, padding:"5px", background:canUndo?"#06111e":"transparent",
                    border:"1px solid #0d1520", color:canUndo?"#6b7e94":"#1a2636",
                    borderRadius:2, cursor:canUndo?"pointer":"default", fontSize:9, ...mono }}>
                  ↩ Undo
                </button>
                <button onClick={onRedo} disabled={!canRedo}
                  style={{ flex:1, padding:"5px", background:canRedo?"#06111e":"transparent",
                    border:"1px solid #0d1520", color:canRedo?"#6b7e94":"#1a2636",
                    borderRadius:2, cursor:canRedo?"pointer":"default", fontSize:9, ...mono }}>
                  ↪ Redo
                </button>
              </div>
              <label style={{ display:"flex", alignItems:"center", gap:5,
                cursor:"pointer", fontSize:8.5, color:"#2a3a4a", padding:"2px 0" }}>
                <input type="checkbox" checked={showHE} onChange={e=>setShowHE(e.target.checked)}/>
                Show half-edges
              </label>
              <button onClick={onClear}
                style={{ padding:"5px", background:"transparent", border:"1px solid #0d1520",
                  color:"#1e2a36", borderRadius:2, cursor:"pointer", fontSize:9, ...mono }}>
                ↺ Clear canvas
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
